/**
 * Handles filtering our city page restaurants
 * Filters by:
 * - city search selected session
 * - selected cuisine
 * - selected other filters e.g. has amex or 4 plus more
 * @param keyValueArray
 * @param search
 */
export const getKeyValuePair = (
  keyValueArray: { key: string; value: string }[],
  search: string | string[] | boolean
) => {
  const all = typeof search === 'boolean';
  const list = [].concat(search);
  return (keyValueArray ?? [])
    .filter((item) => all || list.includes(item.key))
    .reduce(
      (obj, item) => ({
        ...obj,
        [item.key]: item.value,
      }),
      {}
    );
};

// Move these to a common file
export const nameToCamelCase = (name: string) => name[0].toLowerCase() + name.substring(1);

// Array.map for objects
export const objectMap = (obj, fn) =>
  Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));

// Array.map object keys
export const objectKeysMap = (obj, fn) =>
  Object.fromEntries(Object.entries(obj).map(([k, v], i) => [fn(k, i), v]));

/**
 * If `value` is an array then return the first element, otherwise return the `value`. Returns `null` if `value` is an empty array.
 */
export function unArray<T>(value: T | T[]): T | null {
  if (Array.isArray(value)) {
    if (value.length > 0) {
      return value[0];
    }

    return null;
  }

  return value;
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Flattens a nested object into a single-level object with camelCased keys.
 * Each nested property path is concatenated with capitalized keys.
 * Ignores null, undefined, arrays, and functions.
 * @param obj - The object to flatten
 * @param parentKey - The parent key prefix (used internally for recursion)
 * @param result - The accumulator object (used internally for recursion)
 * @returns A flattened object with camelCased keys
 * @example
 * flattenObject({ user: { name: 'John', address: { city: 'NYC' } } })
 * // Returns: { userName: 'John', userAddressCity: 'NYC' }
 */
export function flattenObject(
  obj: object,
  parentKey = '',
  result: Record<string, string> = {}
): Record<string, string> {
  Object.entries(obj).forEach(([key, value]) => {
    const newKey = parentKey ? `${parentKey}${capitalizeFirstLetter(key)}` : key;

    // ignore things that aren't objects or primitive values
    if (
      value === null ||
      value === undefined ||
      Array.isArray(value) ||
      typeof value === 'function'
    ) {
      return;
    }

    if (typeof value === 'object') {
      flattenObject(value, newKey, result);
    } else {
      result[newKey] = value;
    }
  });

  return result;
}

/**
 * Validates if a string is a valid URL
 * @param url - The URL string to validate
 * @returns true if valid URL, false otherwise
 */
export function isValidUrl(url: string): boolean {
  try {
    return Boolean(new URL(url));
  } catch {
    return false;
  }
}

/**
 * Extracts the domain/hostname from a URL string
 * @param url - The full URL string to extract the domain from
 * @returns The hostname (domain) of the URL, or the original string if parsing fails
 * @example
 * getDomainFromUrl('https://www.firsttable.co.nz/restaurants') // returns 'www.firsttable.co.nz' getDomainFromUrl('https://goku.co.nz/') // returns 'goku.co.nz'
 */
export function getDomainFromUrl(url: string): string {
  try {
    const urlObject = new URL(url);
    return urlObject.hostname;
  } catch {
    // If URL parsing fails, return the original string
    return url;
  }
}

/**
 * Flattens an object by stringifying all non-primitive properties.
 * Primitive values (string, number, boolean, null, undefined) are kept as-is.
 * Non-primitive values (objects, arrays, functions) are converted to JSON strings.
 * @param obj - The object to flatten
 * @returns A flattened object with non-primitive values stringified
 * @example
 * flattenObjectByStringifying({ name: 'John', age: 30, address: { city: 'NYC' }, tags: ['dev', 'js'] })
 * // Returns: { name: 'John', age: 30, address: '{"city":"NYC"}', tags: '["dev","js"]' }
 */
export function flattenObjectByStringifying(obj: Record<string, unknown>) {
  const result: Record<string, string | number | boolean> = {};

  Object.entries(obj).forEach(([key, value]) => {
    // ignore nulls and undefined
    if (value === null || value === undefined) {
      return;
    }

    // Check if value is a primitive
    const isPrimitive =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

    if (isPrimitive) {
      result[key] = value;
    } else {
      // Stringify non-primitive values (objects, arrays, functions, etc.)
      try {
        result[key] = JSON.stringify(value);
      } catch (error) {
        // If JSON.stringify fails (e.g., circular references), use string representation
        result[key] = String(value);
      }
    }
  });

  return result;
}
